import { Injectable } from '@angular/core';
import { AssetDto } from '@nusign/picflow-backend-types';
import { BehaviorSubject, Observable } from 'rxjs';
import { Rect } from '../../classes/rect.class';
import { assetSideMenuSize } from '../../consts';
import { canDisplayAsset } from '../../lib/can-display-asset';
import { devicePixelRatio } from '../../lib/device-pixel-ratio';
import { getAssetDimentions } from '../lib/get-asset-dimentions';

export type TransitionObj = {
  asset: AssetDto;
  resolve: (value?: unknown) => void;
  from: Rect;
  to?: Rect;
  direction: 'to' | 'from';
};

@Injectable({
  providedIn: 'root',
})
export class AssetTransitionService {
  currentAsset: AssetDto;
  private _asset = new BehaviorSubject<TransitionObj>(undefined);
  public readonly onAssetChange: Observable<TransitionObj> = this._asset.asObservable();

  private getContainerSize(menuOpen = false, asset: AssetDto) {
    const extraVerticalSpace = asset?.type === 'video' ? 120 : 0;
    if (typeof window === 'undefined') return new Rect(0, 0, 0, 0);
    const documentMode = asset.type === 'document';
    if (window.innerWidth < 900) {
      return new Rect(
        0,
        0,
        window.innerWidth,
        menuOpen ? window.innerHeight * 0.3 : window.innerHeight - extraVerticalSpace
      );
    }

    return new Rect(
      documentMode ? 160 : 0,
      0,
      window.innerWidth - (menuOpen ? assetSideMenuSize : 0) - (documentMode ? 160 : 0),
      window.innerHeight - extraVerticalSpace
    );
  }

  public getAssetPageAssetBounds(asset: AssetDto, menuOpen = false) {
    const extraVerticalSpace = asset?.type === 'video' ? 120 : 0;
    const { width: containerWidth, height: containerHeight, x: containerLeft } = this.getContainerSize(menuOpen, asset);

    let zoom = 1;
    const container = { width: containerWidth, height: containerHeight };
    const containerAspect = container.width / container.height;

    const canDisplay = canDisplayAsset(asset);

    const fullWidth = ['non_preview', 'audio'].includes(asset.type) || !canDisplay;
    const fullHeight = ['non_preview'].includes(asset.type) || !canDisplay;

    const data = getAssetDimentions(asset.version_data, {
      width: (fullWidth ? containerWidth : 250) * devicePixelRatio(),
      height:
        (fullHeight ? containerHeight : asset.type === 'audio' ? containerHeight - 120 : 250) * devicePixelRatio(),
    });

    const aspect = data.width / data.height;
    const vertical = aspect < containerAspect;

    const scaledContainer = {
      width: vertical ? container.height * aspect : container.width,
      height: vertical ? container.height : container.width / aspect,
    };

    const offsetScale = scaledContainer.width / data.width;
    const percent = Math.round(offsetScale * 100 * zoom * devicePixelRatio());

    let containerSize = [containerWidth * zoom, containerHeight * zoom];

    if (percent > 100) {
      const scale = 100 / percent;
      zoom = zoom * scale;
      containerSize = [containerWidth * zoom, containerHeight * zoom];
    }

    const width = Math.round(vertical ? containerSize[1] * aspect : containerSize[0]);
    const height = Math.round(vertical ? containerSize[1] : containerSize[0] / aspect);

    const x = containerLeft + containerWidth / 2 - width / 2;
    const isMultiPageDocument = asset.type === 'document' && asset.version_data.data['images'].length > 1;

    const y = isMultiPageDocument ? 60 : containerHeight / 2 - height / 2 + extraVerticalSpace / 2;

    return new Rect(x, y, width, height);
  }

  public async transitionToAsset(asset: AssetDto, el: HTMLElement, menuOpen = false) {
    const from = Rect.fromDOMRect(el.getBoundingClientRect());
    const to = this.getAssetPageAssetBounds(asset, menuOpen);
    return new Promise(resolve => {
      this._asset.next({ asset, resolve, from, to, direction: 'to' });
    });
  }

  public async transitionFromAsset(asset: AssetDto, from: Rect) {
    return new Promise(resolve => {
      this._asset.next({ asset, resolve, from, direction: 'from' });
    });
  }

  public clear() {
    this._asset.next(undefined);
  }
}
